import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, apiError, eur } from "@/lib/api";
import { StatusBadge, StoreBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Pencil, Trash2, RotateCcw, Ban, ChevronDown, ChevronUp, Send, Eraser, Play, Store as StoreIcon, StickyNote } from "lucide-react";

const ACTIVE_STATES = ["processing", "shipment_created", "waiting_tracking"];

function CourierName({ store, code }) {
  const c = (store?.couriers || []).find((x) => x.service_code === code);
  return <span>{c ? c.name : code || "—"}</span>;
}

export default function WorkPage() {
  const [session, setSession] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [shipping, setShipping] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [cancelItem, setCancelItem] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [storeConflict, setStoreConflict] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const loadSession = useCallback(async () => {
    try {
      const r = await API.get("/session");
      setSession(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    loadSession();
    API.get("/stores").then((r) => setStores(r.data.stores)).catch(() => {});
  }, [loadSession]);

  // polling mentre ci sono ordini in lavorazione: la UI si aggiorna riga per riga
  useEffect(() => {
    if (!session?.items?.some((i) => ACTIVE_STATES.includes(i.status))) return;
    const t = setInterval(loadSession, 2500);
    return () => clearInterval(t);
  }, [session, loadSession]);

  const focusInput = () => setTimeout(() => inputRef.current?.focus(), 50);

  const startSession = async () => {
    if (!selectedStore) return;
    try {
      const r = await API.post("/session/start", { store_id: selectedStore });
      setSession(r.data);
      toast.success(r.data.items?.length ? "Sessione ripresa: gli ordini inseriti sono ancora qui" : "Sessione avviata");
      focusInput();
    } catch (e) {
      if (e?.response?.status === 409) {
        setStoreConflict(apiError(e)); // cambio store con sessione piena: serve conferma esplicita
      } else {
        toast.error(apiError(e));
      }
    }
  };

  const switchStoreConfirm = async () => {
    try {
      await API.delete("/session");
      const r = await API.post("/session/start", { store_id: selectedStore });
      setSession(r.data);
      setStoreConflict(null);
      toast.success("Sessione precedente svuotata, nuova sessione avviata");
      focusInput();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addOrder = async (e) => {
    e.preventDefault();
    const value = input.trim();
    if (!value || adding) return;
    setAdding(true);
    try {
      const r = await API.post("/session/items", { order_name: value });
      setSession(r.data);
      toast.success(`Ordine aggiunto in sessione`);
    } catch (err) {
      toast.error(apiError(err), { duration: 6000 });
    } finally {
      setInput("");
      setAdding(false);
      focusInput();
    }
  };

  const removeItem = async (item) => {
    try {
      const r = await API.delete(`/session/items/${encodeURIComponent(item.order_name)}`);
      setSession(r.data);
      toast.success(`Ordine ${item.order_name} rimosso`);
    } catch (e) {
      toast.error(apiError(e));
    }
    focusInput();
  };

  const prepare = async () => {
    try {
      const r = await API.post("/session/prepare");
      setSession(r.data.session);
      if (r.data.valid === 0) {
        toast.error("Nessun ordine valido da spedire. Controlla gli errori sulle righe.");
        return;
      }
      setConfirmData(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
    focusInput();
  };

  const ship = async () => {
    setShipping(true);
    try {
      const r = await API.post("/session/ship");
      setSession(r.data);
      setConfirmData(null);
      toast.success("Creazione spedizioni avviata");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setShipping(false);
      focusInput();
    }
  };

  const retry = async (item) => {
    try {
      const r = await API.post(`/session/items/${encodeURIComponent(item.order_name)}/retry`);
      setSession(r.data);
      toast.success(`Riprova avviata per ${item.order_name}`);
    } catch (e) {
      toast.error(apiError(e));
    }
    focusInput();
  };

  const cancelShipment = async () => {
    try {
      const r = await API.post(`/shipments/${encodeURIComponent(cancelItem.shipment_id)}/cancel`);
      toast.success(r.data.message);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 8000 });
      setCancelItem(null);
      loadSession();
    } catch (e) {
      toast.error(apiError(e));
    }
    focusInput();
  };

  const clearSession = async () => {
    try {
      await API.delete("/session");
      setSession({ active: false, store: null, items: [], totals: {} });
      setClearConfirm(false);
      toast.success("Sessione svuotata");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // ---------- caricamento: mai mostrare il picker prima di sapere se esiste una sessione ----------
  if (session === null) {
    return (
      <div data-testid="session-loading" className="py-20 text-center text-sm font-semibold text-muted-foreground">
        Caricamento sessione…
      </div>
    );
  }

  // ---------- nessuna sessione: scelta store ----------
  if (!session.active) {
    return (
      <div data-testid="store-picker" className="mx-auto max-w-3xl">
        <h1 className="mb-2 text-3xl font-extrabold tracking-tight">Schermata di lavoro</h1>
        <p className="mb-6 text-muted-foreground">
          Scegli lo store per avviare la sessione. La sessione lavora su UN solo store: un ordine non puo mai finire
          lavorato con le credenziali di un altro store.
        </p>
        {stores.length === 0 ? (
          <div data-testid="no-stores-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-slate-900 text-white">
              <StoreIcon size={26} />
            </div>
            <h2 className="mb-2 text-xl font-bold">Nessuno store collegato</h2>
            <p className="mx-auto mb-6 max-w-md text-sm text-muted-foreground">
              Per iniziare collega il primo store Shopify: ti servono il dominio del negozio, client_id e client_secret
              della sua app Shopify, e l'authCode Spediamo Pro. Dopo il salvataggio usa "Verifica connessione".
            </p>
            <button
              data-testid="go-to-stores-btn"
              onClick={() => navigate("/store")}
              className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700"
            >
              Vai a Store collegati
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {stores.map((s) => (
              <label
                key={s.id}
                data-testid={`store-option-${s.code}`}
                className={`flex cursor-pointer items-center justify-between rounded-xl border-2 bg-white p-5 transition-colors ${
                  selectedStore === s.id ? "border-blue-600" : "border-slate-200 hover:border-slate-400"
                }`}
              >
                <div className="flex items-center gap-4">
                  <input
                    type="radio"
                    name="store"
                    className="h-5 w-5"
                    checked={selectedStore === s.id}
                    onChange={() => setSelectedStore(s.id)}
                  />
                  <div>
                    <div className="flex items-center gap-2 text-lg font-bold">
                      {s.name} <StoreBadge code={s.code} testid={`store-option-code-${s.code}`} />
                    </div>
                    <div className="text-sm text-muted-foreground">{s.shop_domain}</div>
                  </div>
                </div>
                <StatusBadge
                  status={s.status === "connected" ? "completed" : s.status === "error" ? "failed" : "pending"}
                  testid={`store-option-status-${s.code}`}
                />
              </label>
            ))}
            <button
              data-testid="start-session-btn"
              onClick={startSession}
              disabled={!selectedStore}
              className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 text-lg font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              <Play size={20} /> Avvia sessione
            </button>
          </div>
        )}

        {storeConflict && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="switch-store-modal">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <h2 className="mb-3 text-xl font-bold">Cambiare store?</h2>
              <p className="text-sm">{storeConflict}</p>
              <p className="mt-2 rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-800">
                Attenzione: questa azione non e annullabile, gli inserimenti andranno rifatti.
              </p>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  data-testid="switch-store-no"
                  onClick={() => setStoreConflict(null)}
                  className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100"
                >
                  Annulla, mantieni la sessione
                </button>
                <button
                  data-testid="switch-store-yes"
                  onClick={switchStoreConfirm}
                  className="rounded-lg bg-rose-600 px-6 py-3 text-sm font-bold text-white hover:bg-rose-700"
                >
                  Svuota e cambia store
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const store = session.store;
  const totals = session.totals || {};
  const pendingCount = session.items.filter((i) => i.status === "pending").length;
  const estimatedKnown = session.items.some((i) => i.cost_estimated_cents != null || i.cost_cents != null);
  const finalKnown = session.items.some((i) => i.cost_cents != null);
  const toggleSelect = (name) =>
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(name)) s.delete(name);
      else s.add(name);
      return s;
    });
  const allSelected = session.items.length > 0 && selected.size === session.items.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(session.items.map((i) => i.order_name)));

  return (
    <div data-testid="work-screen">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Schermata di lavoro</h1>
          <p className="text-sm text-muted-foreground">
            Store attivo: <strong>{store?.name}</strong> ({store?.code}) — sessione salvata sul server: ricaricare la pagina non perde nulla.
          </p>
        </div>
        <button
          data-testid="clear-session-btn"
          onClick={() => setClearConfirm(true)}
          className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
        >
          <Eraser size={16} /> Svuota sessione
        </button>
      </div>

      {/* campo inserimento sempre attivo */}
      <div className="mb-6 rounded-xl border border-slate-700 bg-gradient-to-r from-slate-900 to-slate-800 p-5 shadow-lg">
        <form onSubmit={addOrder} className="flex items-center gap-3">
          <label htmlFor="order-input" className="whitespace-nowrap text-sm font-semibold uppercase tracking-wider text-slate-400">
            Numero ordine
          </label>
          <input
            id="order-input"
            data-testid="order-input"
            ref={inputRef}
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`es. ${store?.code}142728 — poi INVIO`}
            className="h-14 flex-1 rounded-lg border-2 border-emerald-500/50 bg-slate-950 px-4 font-mono text-xl font-bold tracking-wide text-emerald-400 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/20"
            autoComplete="off"
          />
          <button
            data-testid="add-order-btn"
            type="submit"
            disabled={adding || !input.trim()}
            className="h-14 rounded-lg bg-emerald-600 px-6 text-base font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
          >
            Aggiungi ⏎
          </button>
        </form>
      </div>

      {/* totali di sessione */}
      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="total-orders">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ordini in sessione</div>
          <div className="font-mono text-2xl font-bold">{totals.count ?? 0}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="total-cod">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Totale contrassegni</div>
          <div className="font-mono text-2xl font-bold">{eur(totals.cod_cents)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="total-cost-estimated">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Costo previsto</div>
          <div className="font-mono text-2xl font-bold">{estimatedKnown ? eur(totals.cost_estimated_cents) : "—"}</div>
          {!estimatedKnown && <div className="text-xs text-muted-foreground">non ancora calcolato</div>}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="total-cost-final">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Costo effettivo</div>
          <div className="font-mono text-2xl font-bold">{finalKnown ? eur(totals.cost_cents) : "—"}</div>
          {!finalKnown && <div className="text-xs text-muted-foreground">disponibile dopo la creazione</div>}
        </div>
      </div>

      {/* barra azioni modifica in blocco */}
      {selected.size > 0 && (
        <div data-testid="bulk-actions-bar" className="mb-4 flex items-center justify-between rounded-xl border border-blue-300 bg-blue-50 px-5 py-3">
          <span className="text-sm font-semibold text-blue-900" data-testid="bulk-selected-count">
            {selected.size} {selected.size === 1 ? "ordine selezionato" : "ordini selezionati"}
          </span>
          <div className="flex gap-2">
            <button
              data-testid="bulk-deselect-btn"
              onClick={() => setSelected(new Set())}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-100"
            >
              Deseleziona
            </button>
            <button
              data-testid="bulk-open-btn"
              onClick={() => setBulkOpen(true)}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700"
            >
              Modifica in blocco
            </button>
          </div>
        </div>
      )}

      {/* tabella ordini */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="w-10 p-3">
                <input
                  type="checkbox"
                  data-testid="select-all-checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="h-4 w-4 align-middle"
                  title="Seleziona / deseleziona tutto"
                />
              </th>
              <th className="p-3">Store</th>
              <th className="p-3">Ordine</th>
              <th className="p-3">Cliente</th>
              <th className="p-3">Localita / CAP</th>
              <th className="p-3">Indirizzo</th>
              <th className="p-3">Telefono</th>
              <th className="p-3">Pagamento</th>
              <th className="p-3">Contrassegno</th>
              <th className="p-3">Corriere</th>
              <th className="p-3">Costo</th>
              <th className="p-3">Stato</th>
              <th className="p-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {session.items.length === 0 && (
              <tr>
                <td colSpan={13} className="p-8 text-center text-muted-foreground" data-testid="empty-session">
                  Nessun ordine in sessione. Digita un numero ordine e premi INVIO.
                </td>
              </tr>
            )}
            {session.items.map((item) => (
              <React.Fragment key={item.shopify_order_id}>
                <tr data-testid={`row-${item.order_name}`} className={`border-t ${item.status === "failed" ? "bg-rose-50" : ""}`}>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      data-testid={`select-${item.order_name}`}
                      checked={selected.has(item.order_name)}
                      onChange={() => toggleSelect(item.order_name)}
                      className="h-4 w-4 align-middle"
                    />
                  </td>
                  <td className="p-3">
                    <StoreBadge code={store?.code} testid={`row-store-${item.order_name}`} />
                  </td>
                  <td className="p-3 font-mono font-semibold">
                    {item.order_name}
                    {item.modified && (
                      <span
                        data-testid={`modified-badge-${item.order_name}`}
                        title="Dati modificati rispetto all'originale Shopify"
                        className="ml-2 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-orange-700"
                      >
                        modificato
                      </span>
                    )}
                    {item.note ? (
                      <span
                        data-testid={`note-badge-${item.order_name}`}
                        title={item.note}
                        className="ml-2 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800"
                      >
                        <StickyNote size={11} /> nota
                      </span>
                    ) : null}
                  </td>
                  <td className="p-3">{item.customer_name}</td>
                  <td className="p-3">
                    {item.city}{" "}
                    {/^[A-Z]{2}$/.test(item.province || "") ? (
                      <span className="font-semibold" data-testid={`province-${item.order_name}`}>({item.province})</span>
                    ) : (
                      <span data-testid={`province-missing-${item.order_name}`} className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700">provincia mancante</span>
                    )}{" "}
                    <span className="font-mono text-muted-foreground">{item.zip}</span>
                  </td>
                  <td className="max-w-[200px] truncate p-3" title={item.address}>{item.address}</td>
                  <td className="p-3 font-mono text-xs">{item.phone || "—"}</td>
                  <td className="p-3" data-testid={`payment-${item.order_name}`}>
                    {(item.payment_label || (item.cod_cents != null ? "Contrassegno" : "Pagato")) === "Contrassegno" ? (
                      <span className="rounded-md border border-orange-300 bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">
                        Contrassegno
                      </span>
                    ) : (
                      <span className="rounded-md border border-teal-300 bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800">
                        Pagato
                      </span>
                    )}
                  </td>
                  <td className="p-3 font-mono font-semibold" data-testid={`cod-cell-${item.order_name}`}>
                    {item.cod_cents != null ? eur(item.cod_cents) : "—"}
                    {item.cod_cents != null && (item.cod_cents || 0) !== (item.original?.cod_cents || 0) && (
                      <div className="text-[11px] font-semibold text-orange-700" data-testid={`cod-diff-${item.order_name}`}>
                        originale {item.original?.cod_cents != null ? eur(item.original.cod_cents) : "—"},{" "}
                        {item.cod_cents - (item.original?.cod_cents || 0) >= 0 ? "+" : ""}
                        {eur(item.cod_cents - (item.original?.cod_cents || 0))}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <CourierName store={store} code={item.courier} />
                  </td>
                  <td className="p-3 font-mono">
                    {item.cost_cents != null ? (
                      <span title={`Preventivo: ${eur(item.cost_estimated_cents)}`}>{eur(item.cost_cents)}</span>
                    ) : item.cost_estimated_cents != null ? (
                      <span className="text-muted-foreground" title="Costo preventivato">{eur(item.cost_estimated_cents)}</span>
                    ) : (
                      <span className="text-muted-foreground" title="Preventivo non ancora calcolato: si calcola con 'Spedisci il lotto'">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <StatusBadge status={item.status} testid={`status-badge-${item.order_name}`} />
                    {item.tracking && (
                      <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                        {item.tracking_url ? (
                          <a href={item.tracking_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline" data-testid={`tracking-link-${item.order_name}`}>
                            {item.tracking}
                          </a>
                        ) : (
                          item.tracking
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {(item.status === "pending" || item.status === "failed") && (
                        <button
                          data-testid={`edit-btn-${item.order_name}`}
                          title="Modifica dati"
                          onClick={() => setEditItem(item)}
                          className="rounded-md border border-slate-300 p-2 text-slate-600 transition-colors hover:bg-slate-100"
                        >
                          <Pencil size={15} />
                        </button>
                      )}
                      {(item.status === "failed" || item.status === "cancelled") && (
                        <button
                          data-testid={`retry-btn-${item.order_name}`}
                          title="Correggi e riprova (solo questo ordine)"
                          onClick={() => retry(item)}
                          className="rounded-md border border-blue-300 bg-blue-50 p-2 text-blue-700 transition-colors hover:bg-blue-100"
                        >
                          <RotateCcw size={15} />
                        </button>
                      )}
                      {item.shipment_id && ["shipment_created", "waiting_tracking", "completed", "failed"].includes(item.status) && (
                        <button
                          data-testid={`cancel-ship-btn-${item.order_name}`}
                          title="Annulla spedizione"
                          onClick={() => setCancelItem(item)}
                          className="rounded-md border border-orange-300 bg-orange-50 p-2 text-orange-700 transition-colors hover:bg-orange-100"
                        >
                          <Ban size={15} />
                        </button>
                      )}
                      {["pending", "failed", "cancelled"].includes(item.status) && (
                        <button
                          data-testid={`remove-btn-${item.order_name}`}
                          title="Rimuovi dalla sessione"
                          onClick={() => removeItem(item)}
                          className="rounded-md border border-slate-300 p-2 text-slate-600 transition-colors hover:bg-rose-50 hover:text-rose-700"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {item.error && (
                  <tr className="border-t bg-rose-50" data-testid={`error-row-${item.order_name}`}>
                    <td colSpan={13} className="px-3 py-2">
                      <div className="flex items-start justify-between gap-4">
                        <div className="text-sm font-semibold text-rose-800">{item.error}</div>
                        {item.error_raw && (
                          <button
                            data-testid={`error-detail-toggle-${item.order_name}`}
                            onClick={() => setExpanded((p) => ({ ...p, [item.order_name]: !p[item.order_name] }))}
                            className="flex items-center gap-1 whitespace-nowrap rounded border border-rose-300 px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                          >
                            Dettaglio tecnico API {expanded[item.order_name] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </button>
                        )}
                      </div>
                      {expanded[item.order_name] && item.error_raw && (
                        <pre
                          data-testid={`error-detail-${item.order_name}`}
                          className="mt-2 max-h-48 overflow-auto rounded-lg bg-slate-950 p-3 font-mono text-xs text-rose-200"
                        >
                          {typeof item.error_raw === "string" ? item.error_raw : JSON.stringify(item.error_raw, null, 2)}
                        </pre>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          data-testid="ship-batch-btn"
          onClick={prepare}
          disabled={pendingCount === 0}
          className="flex h-14 items-center gap-2 rounded-xl bg-blue-600 px-8 text-lg font-bold text-white shadow-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          <Send size={20} /> Spedisci il lotto ({pendingCount})
        </button>
      </div>

      {/* modale modifica in blocco contrassegno */}
      {bulkOpen && (
        <BulkCodModal
          items={session.items}
          totals={totals}
          selectedNames={[...selected]}
          onClose={() => setBulkOpen(false)}
          onApplied={(res) => {
            setSession(res.session);
            setBulkOpen(false);
            setSelected(new Set());
            toast.success(`Contrassegno aggiornato su ${res.modified.length} ordini`);
            if (res.skipped?.length) {
              toast.warning(
                `Esclusi perche' gia' in lavorazione o spediti: ${res.skipped.map((s) => s.order_name).join(", ")}`,
                { duration: 8000 }
              );
            }
            focusInput();
          }}
        />
      )}

      {/* modale conferma finale */}
      {confirmData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="confirm-ship-modal">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-xl font-bold">Conferma spedizione</h2>
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
              <div className="flex justify-between"><span>Spedizioni da creare</span><strong className="font-mono" data-testid="confirm-count">{confirmData.valid}</strong></div>
              <div className="flex justify-between"><span>Totale contrassegni</span><strong className="font-mono" data-testid="confirm-cod">{eur(confirmData.total_cod_cents)}</strong></div>
              <div className="flex justify-between"><span>Costo previsto (IVA esclusa)</span><strong className="font-mono" data-testid="confirm-cost">{eur(confirmData.total_cost_cents)}</strong></div>
              <div className="flex justify-between">
                <span>Corrieri</span>
                <strong className="font-mono" data-testid="confirm-couriers">
                  {Object.entries(confirmData.by_courier).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
                </strong>
              </div>
            </div>
            {confirmData.errors.length > 0 && (
              <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm" data-testid="confirm-errors">
                <strong className="text-rose-800">{confirmData.errors.length} ordini esclusi per errori:</strong>
                <ul className="mt-1 list-inside list-disc text-rose-700">
                  {confirmData.errors.map((e) => (
                    <li key={e.order_name}><span className="font-mono">{e.order_name}</span> — {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Da qui si spende: le spedizioni vengono create davvero su Spediamo Pro. Gli ordini falliti non bloccano gli altri.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                data-testid="confirm-ship-cancel"
                onClick={() => setConfirmData(null)}
                className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100"
              >
                Annulla
              </button>
              <button
                data-testid="confirm-ship-btn"
                onClick={ship}
                disabled={shipping}
                className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {shipping ? "Creazione in corso…" : "Conferma e spedisci"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* modale modifica dati */}
      {editItem && (
        <EditItemModal
          item={editItem}
          store={store}
          onClose={() => {
            setEditItem(null);
            focusInput();
          }}
          onSaved={(s) => {
            setSession(s);
            setEditItem(null);
            focusInput();
          }}
        />
      )}

      {/* conferma annullamento spedizione */}
      {cancelItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="cancel-ship-modal">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-3 text-xl font-bold">Annulla spedizione</h2>
            <p className="text-sm">
              Annullare la spedizione dell'ordine <strong className="font-mono">{cancelItem.order_name}</strong>?
              Verra annullata su Spediamo Pro e l'evasione Shopify verra rimossa: l'ordine torna Inevaso e di nuovo processabile.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                data-testid="cancel-ship-no"
                onClick={() => setCancelItem(null)}
                className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100"
              >
                No, mantieni
              </button>
              <button
                data-testid="cancel-ship-yes"
                onClick={cancelShipment}
                className="rounded-lg bg-orange-600 px-6 py-3 text-sm font-bold text-white hover:bg-orange-700"
              >
                Si, annulla spedizione
              </button>
            </div>
          </div>
        </div>
      )}

      {/* conferma svuota sessione */}
      {clearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="clear-session-modal">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="mb-3 text-xl font-bold">Svuota sessione</h2>
            <p className="text-sm">Verranno rimossi tutti gli ordini dalla sessione di lavoro. Le spedizioni gia create restano attive.</p>
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="clear-session-no" onClick={() => setClearConfirm(false)} className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100">
                Annulla
              </button>
              <button data-testid="clear-session-yes" onClick={clearSession} className="rounded-lg bg-rose-600 px-6 py-3 text-sm font-bold text-white hover:bg-rose-700">
                Svuota
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditItemModal({ item, store, onClose, onSaved }) {
  const [form, setForm] = useState({
    address: item.address || "",
    zip: item.zip || "",
    city: item.city || "",
    province: item.province || "",
    phone: item.phone || "",
    note: item.note || "",
    cod: item.cod_cents != null ? (item.cod_cents / 100).toFixed(2).replace(".", ",") : "",
    courier: item.courier || store?.default_courier || "SDAEXP",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const o = item.original || {};

  const changed = (key, origVal) => {
    const orig = key === "cod" ? (o.cod_cents != null ? (o.cod_cents / 100).toFixed(2).replace(".", ",") : "") : o[key] ?? "";
    return String(form[key]) !== String(orig);
  };

  const save = async () => {
    setError("");
    if (!/^[A-Z]{2}$/.test((form.province || "").trim())) {
      setError("Provincia non valida: serve la sigla di 2 lettere maiuscole (es. RM), non il nome per esteso.");
      return;
    }
    setSaving(true);
    try {
      const r = await API.put(`/session/items/${encodeURIComponent(item.order_name)}`, form);
      toast.success(`Ordine ${item.order_name} aggiornato`);
      onSaved(r.data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, testid, extra = {}) => (
    <div>
      <label className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {changed(key) && <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-orange-700">modificato</span>}
      </label>
      <input
        data-testid={testid}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className={`h-11 w-full rounded-lg border px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${
          changed(key) ? "border-orange-400 bg-orange-50" : "border-input"
        }`}
        {...extra}
      />
      {changed(key) && (
        <div className="mt-1 text-xs text-muted-foreground">
          Originale: <span className="font-mono">{key === "cod" ? (o.cod_cents != null ? (o.cod_cents / 100).toFixed(2).replace(".", ",") : "—") : o[key] || "—"}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="edit-item-modal">
      <div className="w-full max-w-xl rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-1 text-xl font-bold">Modifica ordine <span className="font-mono">{item.order_name}</span></h2>
        <p className="mb-4 text-xs text-muted-foreground">Le modifiche valgono solo per questa spedizione e restano evidenziate rispetto ai dati originali.</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">{field("address", "Indirizzo", "edit-address")}</div>
          {field("zip", "CAP", "edit-zip")}
          {field("city", "Citta", "edit-city")}
          {field("province", "Provincia (sigla, es. RM)", "edit-province", {
            placeholder: "es. RM",
            onChange: (e) => setForm((f) => ({ ...f, province: e.target.value.toUpperCase().replace(/[^A-Z]/g, "") })),
          })}
          {field("phone", "Telefono", "edit-phone")}
          {field("cod", "Contrassegno (€, vuoto = nessuno)", "edit-cod", { placeholder: "es. 79,90" })}
          <div className="col-span-2">
            <label className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Note consegna
              {changed("note") && <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-orange-700">modificato</span>}
              <span className="ml-auto font-normal normal-case">{form.note.length}/500</span>
            </label>
            <textarea
              data-testid="edit-note"
              rows={2}
              maxLength={500}
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              placeholder="es. consegnare al vicino, citofono rotto, chiamare prima…"
              className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${
                changed("note") ? "border-orange-400 bg-orange-50" : "border-input"
              }`}
            />
            {form.note.length > 20 && (
              <div className="mt-1 text-[11px] font-semibold text-amber-700" data-testid="edit-note-truncation-warning">
                Il corriere accetta max 20 caratteri: a Spediamo Pro sara inviata «{form.note.slice(0, 20)}». La nota completa resta salvata qui.
              </div>
            )}
            {changed("note") && (
              <div className="mt-1 text-xs text-muted-foreground">
                Originale: <span className="font-mono">{o.note || "—"}</span>
              </div>
            )}
          </div>
          <div className="col-span-2">
            <label className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Corriere
              {changed("courier") && <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-orange-700">modificato</span>}
            </label>
            <select
              data-testid="edit-courier"
              value={form.courier}
              onChange={(e) => setForm((f) => ({ ...f, courier: e.target.value }))}
              className={`h-11 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-blue-500 ${
                changed("courier") ? "border-orange-400 bg-orange-50" : "border-input"
              }`}
            >
              {(store?.couriers || []).map((c) => (
                <option key={c.service_code} value={c.service_code}>
                  {c.name} ({c.service_code})
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && (
          <div data-testid="edit-error" className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">
            {error}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <button data-testid="edit-cancel" onClick={onClose} className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100">
            Annulla
          </button>
          <button
            data-testid="edit-save"
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? "Salvataggio…" : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkCodModal({ items, totals, selectedNames, onClose, onApplied }) {
  const [amount, setAmount] = useState(() => localStorage.getItem("bulk_cod_last") || "3,00");
  const [step, setStep] = useState("input");
  const [error, setError] = useState("");
  const [applying, setApplying] = useState(false);

  const parseAmt = (s) => {
    const m = /^(\d+)([.,](\d{1,2}))?$/.exec(String(s).trim().replace("€", "").replace(" ", ""));
    return m ? parseInt(m[1], 10) * 100 + parseInt((m[3] || "0").padEnd(2, "0"), 10) : null;
  };
  const addCents = parseAmt(amount);
  const chosen = items.filter((i) => selectedNames.includes(i.order_name));
  const applicable = chosen.filter((i) => ["pending", "failed"].includes(i.status));
  const excluded = chosen.filter((i) => !["pending", "failed"].includes(i.status));
  const example = applicable[0];
  const newTotal = (totals.cod_cents || 0) + (addCents || 0) * applicable.length;

  const apply = async () => {
    setApplying(true);
    setError("");
    try {
      localStorage.setItem("bulk_cod_last", amount);
      const r = await API.post("/session/bulk-cod", { order_names: applicable.map((i) => i.order_name), amount });
      onApplied(r.data);
    } catch (e) {
      setError(apiError(e));
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-testid="bulk-modal">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
        <h2 className="mb-1 text-xl font-bold">Modifica in blocco — Aggiungi al contrassegno</h2>
        <p className="mb-4 rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-800">
          Attenzione: l'operazione NON e' idempotente. Applicarla due volte aggiunge l'importo due volte.
        </p>
        {step === "input" ? (
          <>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Importo da aggiungere (€) — accetta virgola e punto
            </label>
            <input
              data-testid="bulk-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
              placeholder="es. 3,00"
              className="h-12 w-full rounded-lg border border-input px-4 font-mono text-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
            {amount && addCents === null && (
              <div className="mt-2 text-sm font-semibold text-rose-700" data-testid="bulk-amount-error">
                Formato non valido (usa ad es. 3,00)
              </div>
            )}
            {excluded.length > 0 && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="bulk-excluded-preview">
                {excluded.length} selezionati non modificabili (gia' in lavorazione o spediti) e verranno esclusi:{" "}
                {excluded.map((i) => i.order_name).join(", ")}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="bulk-cancel" onClick={onClose} className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100">
                Annulla
              </button>
              <button
                data-testid="bulk-next"
                disabled={!addCents || applicable.length === 0}
                onClick={() => setStep("confirm")}
                className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Avanti
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
              <div className="flex justify-between">
                <span>Ordini che verranno modificati</span>
                <strong className="font-mono" data-testid="bulk-count">{applicable.length}</strong>
              </div>
              <div className="flex justify-between">
                <span>Importo aggiunto a ciascuno</span>
                <strong className="font-mono" data-testid="bulk-add">{eur(addCents)}</strong>
              </div>
              {example && (
                <div className="flex justify-between" data-testid="bulk-example">
                  <span>Esempio</span>
                  <strong className="font-mono">
                    {example.order_name}: {eur(example.cod_cents || 0)} → {eur((example.cod_cents || 0) + addCents)}
                  </strong>
                </div>
              )}
              <div className="flex justify-between">
                <span>Nuovo totale contrassegni della sessione</span>
                <strong className="font-mono" data-testid="bulk-new-total">{eur(newTotal)}</strong>
              </div>
            </div>
            {excluded.length > 0 && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="bulk-excluded">
                Esclusi perche' non modificabili: {excluded.map((i) => i.order_name).join(", ")}
              </div>
            )}
            {error && (
              <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800" data-testid="bulk-error">
                {error}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="bulk-back" onClick={() => setStep("input")} className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold hover:bg-slate-100">
                Indietro
              </button>
              <button
                data-testid="bulk-confirm-btn"
                onClick={apply}
                disabled={applying}
                className="rounded-lg bg-orange-600 px-6 py-3 text-sm font-bold text-white hover:bg-orange-700 disabled:opacity-60"
              >
                {applying ? "Applicazione…" : `Applica a ${applicable.length} ordini`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
